/**
 * Plugin registry for storing and retrieving action type plugins with validation and metadata extraction.
 */
import { zodToJsonSchema } from "zod-to-json-schema";
// ════════════════════════════════════════════════════════════════════════════
// PLUGIN REGISTRY
// ════════════════════════════════════════════════════════════════════════════
/**
 * Global registry for action type plugins.
 * Plugins are registered at module initialization time.
 */
const pluginRegistry = new Map();
/**
 * Register an action type plugin.
 * @param plugin - The plugin to register
 * @throws Error if a plugin with the same slug is already registered
 * @example
 * ```typescript
 * import { registerActionPlugin } from "@nucleus/action-plugins";
 * import { inputFormPlugin } from "./plugins/input-form";
 *
 * registerActionPlugin(inputFormPlugin);
 * ```
 */
export function registerActionPlugin(plugin) {
    if (pluginRegistry.has(plugin.slug)) {
        throw new Error(`Action type plugin "${plugin.slug}" is already registered. ` +
            `Each plugin must have a unique slug.`);
    }
    // Store as generic plugin (loses type info but allows mixed storage)
    pluginRegistry.set(plugin.slug, plugin);
}
/**
 * Get a registered plugin by its slug.
 * @param slug - The plugin's unique identifier
 * @returns The plugin if found, undefined otherwise
 */
export function getActionPlugin(slug) {
    return pluginRegistry.get(slug);
}
/**
 * Get a typed plugin by its slug.
 * Use this when you know the plugin's types at compile time.
 * @param slug - The plugin's unique identifier
 * @returns The plugin if found, undefined otherwise
 */
export function getTypedActionPlugin(slug) {
    return pluginRegistry.get(slug);
}
/**
 * Get all registered plugins.
 * @returns Array of all registered plugins
 */
export function getAllActionPlugins() {
    return Array.from(pluginRegistry.values());
}
/**
 * Get all plugin slugs.
 * @returns Array of all registered plugin slugs
 */
export function getActionPluginSlugs() {
    return Array.from(pluginRegistry.keys());
}
/**
 * Check if a plugin is registered.
 * @param slug - The plugin's unique identifier
 * @returns true if the plugin is registered
 */
export function hasActionPlugin(slug) {
    return pluginRegistry.has(slug);
}
/**
 * Clear all registered plugins.
 * Primarily useful for testing.
 */
export function clearActionPlugins() {
    pluginRegistry.clear();
}
// ════════════════════════════════════════════════════════════════════════════
// METADATA EXTRACTION
// ════════════════════════════════════════════════════════════════════════════
/**
 * Convert a plugin's Zod schema to JSON Schema for database storage.
 * @param plugin - The plugin to extract JSON schema from
 * @returns JSON Schema representation of the config schema
 */
export function getPluginJsonSchema(plugin) {
    try {
        return zodToJsonSchema(plugin.configSchema, {
            $refStrategy: "none",
            target: "jsonSchema7",
        });
    }
    catch {
        // Fallback for schemas that can't be converted
        return { type: "object" };
    }
}
/**
 * Extract serializable metadata from a plugin for database storage.
 * @param plugin - The plugin to extract metadata from
 * @returns Metadata suitable for database storage
 */
export function getPluginMetadata(plugin) {
    return {
        slug: plugin.slug,
        name: plugin.name,
        description: plugin.description,
        type: plugin.type,
        completionType: plugin.completionType,
        configSchema: getPluginJsonSchema(plugin),
        defaultConfig: plugin.defaultConfig,
        order: plugin.order ?? 0,
        version: plugin.version,
        author: plugin.author,
    };
}
/**
 * Get metadata for all registered plugins.
 * @returns Array of metadata for all registered plugins
 */
export function getAllPluginMetadata() {
    return getAllActionPlugins().map(getPluginMetadata);
}
// ════════════════════════════════════════════════════════════════════════════
// VALIDATION HELPERS
// ════════════════════════════════════════════════════════════════════════════
/**
 * Validate a configuration object against a plugin's schema.
 * @param slug - The plugin's slug
 * @param config - The configuration to validate
 * @returns Validation result with parsed data or errors
 */
export function validatePluginConfig(slug, config) {
    const plugin = getActionPlugin(slug);
    if (!plugin) {
        return { success: false, errors: [`Unknown plugin: ${slug}`] };
    }
    const result = plugin.configSchema.safeParse(config);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return {
        success: false,
        errors: result.error.errors.map((e) => `${e.path.join(".")}: ${e.message}`),
    };
}
/**
 * Validate execution data against a plugin's data schema.
 * @param slug - The plugin's slug
 * @param data - The execution data to validate
 * @returns Validation result with parsed data or errors
 */
export function validatePluginData(slug, data) {
    const plugin = getActionPlugin(slug);
    if (!plugin) {
        return { success: false, errors: [`Unknown plugin: ${slug}`] };
    }
    const result = plugin.dataSchema.safeParse(data);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return {
        success: false,
        errors: result.error.errors.map((e) => `${e.path.join(".")}: ${e.message}`),
    };
}
//# sourceMappingURL=registry.js.map